import { useState, useEffect } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalFooter,
   ModalCloseButton,
   useColorModeValue,
} from '@chakra-ui/react';
import axios from '../../../../axios';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from 'contexts';
import { DAYBOOK_TYPE } from 'constants';
import { getDaybookForPayments } from '../../../../redux/actions/daybook';
import { getAccount } from '../../../../redux/actions/account';
import AddEditForm from 'views/admin/masters/Pages/CustomerMaster/AddEditForm';
import { getVoucher } from '../../../../redux/actions/voucher';

export default function BulkUpdateSuspenseAccount({
   isBulkUpdateSuspenseAccount,
   onClose,
   handleBulkUpdateSuspenseAccount,
}) {
   const dispatch = useDispatch();
   const [findName, setFindName] = useState('');
   const [accountName, setAccountName] = useState('');
   const accountsData = useSelector((state) => state.account.list || []);
   const [bankStatements, setBankStatements] = useState([]);
   const [selectedStatements, setSelectedStatements] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isImporting, setIsImporting] = useState(false);
   const { user } = useUser();
   const [message, setMessage] = useState('');
   const [selectedAccountId, setSelectedAccountId] = useState('');
   const [isOpen, setIsOpen] = useState(false);

   const userCompanyId = Number(user?.loginhistory?.companyId);
   const localCompanyId = Number(localStorage.getItem('companyId'));
   const validCompanyId =
      !isNaN(userCompanyId) && userCompanyId > 0
         ? userCompanyId
         : !isNaN(localCompanyId) && localCompanyId > 0
            ? localCompanyId
            : null;

   // Refactored Effect 1: Fetch initial data (Daybooks and Suspense Account)
   useEffect(() => {
      if (validCompanyId) {
         dispatch(getDaybookForPayments({ type: DAYBOOK_TYPE.BANK, companyId: validCompanyId }));
         dispatch(getAccount({ compId: validCompanyId, status: true }));
      } else {
         console.warn('Skipping initial data fetch: Invalid companyId');
      }
   }, [dispatch, validCompanyId]);

   const handleAccountSelection = (e) => {
      const selectedAccountId = e.target.value;
      const selectedAccount = accountsData.find(
         (account) => (account.id || account.value || account.accountId) === selectedAccountId,
      );

      setSelectedAccountId(selectedAccountId);
      setAccountName(selectedAccount ? selectedAccount.name : '');
   };

   const handleSave = () => {
      // Close the Add/Edit form modal
      setIsOpen(false);
      // Re-fetch the suspense account to get the newly created one
      if (validCompanyId) {
         // dispatch(getVoucher({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
      }
      // Give feedback to the user
      setMessage('Suspense Account created. Please try importing again.');
   };

   const extractBankStatement = async () => {
      if (!selectedAccountId) {
         setMessage('Please select Account Name');
         return;
      }

      setIsLoading(true);
      setMessage('');

      try {
         const params = {
            accountName: 'SUSPENCE',
            userNote: findName,
            isImported: 1,
            companyId: validCompanyId,
            page: 1,
            limit: 10,
            sortBy: 'id',
            sortOrder: 'desc',
         };

         const response = await axios.get('/voucher/all', {
            params: params,
         });

         const data = response.data.data;
         // Filter out records with 0 amount
         const filteredData = data
            ? data.filter((statement) => {
               const amount = Number(statement.amount);
               return amount !== 0 && !isNaN(amount);
            })
            : [];

         setBankStatements(filteredData);
         // setBankStatements(data || []);
         setSelectedStatements({}); // Reset selections when new data is loaded
      } catch (error) {
         setMessage(`Error: ${error.response.data.message}`);
      } finally {
         setIsLoading(false);
      }
   };

   // Handle individual checkbox selection
   const handleStatementSelection = (statementId, isChecked) => {
      setSelectedStatements((prev) => ({
         ...prev,
         [statementId]: isChecked,
      }));
   };

   // Handle select all checkbox
   const handleSelectAll = (isChecked) => {
      const newSelectedStatements = {};
      if (isChecked) {
         bankStatements.forEach((statement) => {
            newSelectedStatements[statement.id] = true;
         });
      }
      setSelectedStatements(newSelectedStatements);
   };

   // Check if all statements are selected
   const isAllSelected =
      bankStatements.length > 0 && bankStatements.every((statement) => selectedStatements[statement.id]);

   // Get selected statement IDs (excluding records with 0 amount)
   const getSelectedStatementIds = () => {
      return Object.keys(selectedStatements)
         .filter((id) => selectedStatements[id])
         .map((id) => parseInt(id))
         .filter((id) => {
            // Find the statement and ensure it doesn't have 0 amount
            const statement = bankStatements.find((s) => s.id === id);
            return statement && Number(statement.amount) !== 0;
         });
   };

   // Handle find and replace API call
   const handleFindAndReplace = async () => {
      const selectedIds = getSelectedStatementIds();

      if (selectedIds.length === 0) {
         setMessage('Please select at least one statement to update');
         return;
      }

      if (!selectedAccountId) {
         setMessage('Please select a target account');
         return;
      }

      // Enhanced validation for selectedAccountId
      if (!selectedAccountId) {
         setMessage('Selected account ID is missing. Please select a valid account.');
         console.error('selectedAccountId is empty:', selectedAccountId);
         console.error('accountName:', accountName);
         console.error('accountsData:', accountsData);
         return;
      }

      setIsImporting(true);
      setMessage('');

      try {
         // Enhanced payload with additional debugging info
         const payload = {
            statementIds: selectedIds,
            newAccountId: parseInt(selectedAccountId),
            companyId: validCompanyId,
         };

         const response = await axios.post('/voucher/bulk-suspense-update-account', payload);

         setMessage('Successfully updated selected statements');
         // Refresh the data after successful update
         extractBankStatement();
      } catch (error) {
         setMessage(`Error: ${error.response?.data?.message || error.message}`);
      } finally {
         setIsImporting(false);
      }
   };

   // Format amount based on transaction type
   const formatAmount = (amount, txnType) => {
      const formattedAmount = Number(amount).toLocaleString('en-IN', {
         style: 'currency',
         currency: 'INR',
         minimumFractionDigits: 2,
      });

      return txnType === 1 ? `${formattedAmount} (Credit)` : `${formattedAmount} (Debit)`;
   };

   const bgColor = useColorModeValue('white', 'gray.800');
   const headerBgColor = useColorModeValue('blue.50', 'blue.900');

   const selectedCount = getSelectedStatementIds().length;

   return (
      <Modal
         isOpen={isBulkUpdateSuspenseAccount}
         onClose={onClose}
         size="6xl"
         isCentered
         scrollBehavior="inside"
         borderRadius="lg"
      >
         <ModalOverlay />
         <ModalContent bg={bgColor}>

            {/* ── Modal Header — Chakra shell, Tailwind styling inside ── */}
            <ModalHeader className="!bg-blue-50 !border-b !border-blue-100 !px-6 !py-4">
               <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 shrink-0">
                     <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                     </svg>
                  </div>
                  <span className="text-sm font-semibold text-gray-800 tracking-tight">
                     Bulk Update Suspense Account
                  </span>
               </div>
            </ModalHeader>
            <ModalCloseButton />

            {/* ── Modal Body — Chakra shell, Tailwind content ── */}
            <ModalBody className="!p-6">
               <div className="flex flex-col gap-6">

                  {/* ─── Filter / Search Section ─── */}
                  <div className="flex flex-col gap-4">

                     {/* Input row */}
                     <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">

                        {/* Enter Account Name */}
                        <div className="flex flex-col gap-1.5">
                           <label className="text-sm font-medium text-gray-700">
                              Enter Account Name
                           </label>
                           <input
                              type="text"
                              placeholder="Enter Account name"
                              value={findName}
                              onChange={(e) => setFindName(e.target.value)}
                              className="w-full px-3 py-2 text-sm text-gray-800 bg-white border border-gray-300 rounded-lg shadow-sm placeholder-gray-400
                                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                           />
                        </div>

                        {/* Select Account */}
                        <div className="flex flex-col gap-1.5">
                           <label className="text-sm font-medium text-gray-700">
                              Select Account
                           </label>
                           <select
                              value={selectedAccountId}
                              onChange={handleAccountSelection}
                              className="w-full px-3 py-2 text-sm text-gray-800 bg-white border border-gray-300 rounded-lg shadow-sm
                                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                         transition-all duration-150 appearance-none cursor-pointer"
                           >
                              <option value="" disabled>Choose an Account</option>
                              {accountsData.map((bank, index) => (
                                 <option
                                    key={`${bank.id || bank.value}-${index}`}
                                    value={bank.id || bank.value || bank.accountId}
                                 >
                                    {bank.name}
                                 </option>
                              ))}
                           </select>
                        </div>

                        {/* Find Button */}
                        <button
                           onClick={extractBankStatement}
                           disabled={isLoading || !selectedAccountId}
                           className="flex items-center justify-center gap-2 px-5 py-2 text-sm font-medium text-white
                                      bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 active:bg-blue-800
                                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 whitespace-nowrap"
                        >
                           {isLoading ? (
                              <>
                                 <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                 </svg>
                                 Finding...
                              </>
                           ) : (
                              <>
                                 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                                 </svg>
                                 Find
                              </>
                           )}
                        </button>
                     </div>

                     {/* Debug Info */}
                     {selectedAccountId && (
                        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500 space-y-0.5">
                           <p className="font-semibold text-gray-600">Debug Info:</p>
                           <p>Selected Account Name: <span className="text-gray-700">{accountName}</span></p>
                           <p>Selected Account ID: <span className="text-gray-700">{selectedAccountId}</span></p>
                        </div>
                     )}

                     {/* Alert Message */}
                     {message && (
                        <div
                           className={`flex items-start gap-2.5 px-4 py-3 rounded-lg text-sm font-medium border ${message.includes('Error')
                                 ? 'bg-red-50 border-red-200 text-red-700'
                                 : 'bg-green-50 border-green-200 text-green-700'
                              }`}
                        >
                           {message.includes('Error') ? (
                              <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                 <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" />
                              </svg>
                           ) : (
                              <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                 <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                           )}
                           {message}
                        </div>
                     )}
                  </div>

                  {/* ─── Divider ─── */}
                  <hr className="border-gray-200" />

                  {/* ─── Bank Statements Table ─── */}
                  {bankStatements.length > 0 && (
                     <div className="flex flex-col gap-3">

                        {/* Table meta */}
                        <div className="flex items-center justify-between">
                           <p className="text-sm font-semibold text-gray-800">
                              Bank Statements
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                 {bankStatements.length} records
                              </span>
                           </p>
                           <p className="text-xs text-gray-400">
                              Selected:{' '}
                              <span className="font-semibold text-gray-600">{selectedCount}</span>
                              {' '}of {bankStatements.length}
                           </p>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                           <table className="min-w-full text-sm">
                              <thead>
                                 <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-4 py-3 text-left w-10">
                                       <input
                                          type="checkbox"
                                          checked={isAllSelected}
                                          onChange={(e) => handleSelectAll(e.target.checked)}
                                          className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer focus:ring-2 focus:ring-blue-500"
                                       />
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                       Account Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                       User Note
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                       Amount
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                       Transaction Date
                                    </th>
                                 </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-100">
                                 {bankStatements.map((statement, idx) => (
                                    <tr
                                       key={statement.id}
                                       className={`transition-colors duration-100 ${selectedStatements[statement.id]
                                             ? 'bg-blue-50'
                                             : idx % 2 === 0
                                                ? 'bg-white hover:bg-gray-50'
                                                : 'bg-gray-50/40 hover:bg-gray-100/60'
                                          }`}
                                    >
                                       {/* Checkbox */}
                                       <td className="px-4 py-3">
                                          <input
                                             type="checkbox"
                                             checked={selectedStatements[statement.id] || false}
                                             onChange={(e) =>
                                                handleStatementSelection(statement.id, e.target.checked)
                                             }
                                             className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer focus:ring-2 focus:ring-blue-500"
                                          />
                                       </td>

                                       {/* Account Name Badge */}
                                       <td className="px-4 py-3">
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                             {statement.accountName}
                                          </span>
                                       </td>

                                       {/* User Note */}
                                       <td className="px-4 py-3 max-w-[300px]">
                                          <span
                                             className="block text-xs text-gray-600 truncate"
                                             title={statement.userNote || 'N/A'}
                                          >
                                             {statement.userNote || (
                                                <span className="text-gray-400 italic">N/A</span>
                                             )}
                                          </span>
                                       </td>

                                       {/* Amount */}
                                       <td className="px-4 py-3 whitespace-nowrap">
                                          <span
                                             className={`text-xs font-semibold ${statement.txnType === 1 ? 'text-green-600' : 'text-red-600'
                                                }`}
                                          >
                                             {formatAmount(statement.amount, statement.txnType)}
                                          </span>
                                       </td>

                                       {/* Transaction Date */}
                                       <td className="px-4 py-3 whitespace-nowrap">
                                          <span className="text-xs text-gray-600">
                                             {new Date(statement.txnDate).toLocaleDateString('en-IN')}
                                          </span>
                                       </td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     </div>
                  )}

                  {/* Empty State */}
                  {bankStatements.length === 0 && !isLoading && selectedAccountId && (
                     <div className="flex flex-col items-center justify-center py-14 text-gray-400 gap-3">
                        <svg
                           className="w-12 h-12 opacity-30"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                           strokeWidth={1.5}
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                           />
                        </svg>
                        <p className="text-sm font-medium">No bank statements found</p>
                        <p className="text-xs">Try adjusting the filters and clicking Find</p>
                     </div>
                  )}

               </div>
            </ModalBody>

            {/* ── Modal Footer — Chakra shell, Tailwind buttons ── */}
            <ModalFooter className="!border-t !border-gray-200 !bg-gray-50 !px-6 !py-4 !gap-3 !justify-end">

               {/* Find and Replace */}
               <button
                  onClick={handleFindAndReplace}
                  disabled={isImporting || selectedCount === 0 || !selectedAccountId}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white
                             bg-green-600 rounded-lg shadow-sm hover:bg-green-700 active:bg-green-800
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
               >
                  {isImporting ? (
                     <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Updating...
                     </>
                  ) : (
                     <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                           <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Find and Replace {selectedCount > 0 && `(${selectedCount})`}
                     </>
                  )}
               </button>

               {/* Close */}
               <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300
                             rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-800 active:bg-gray-100
                             transition-colors duration-150"
               >
                  Close
               </button>

            </ModalFooter>
         </ModalContent>
      </Modal>
   );
}